import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { authApi } from "@/lib/api/endpoints";
import { isApiError } from "@/lib/api/errors";
import { refreshSession } from "@/lib/api/client";
import { storageKeys, tokens, writeActiveBusinessId } from "@/lib/api/tokens";
import type { AuthSession, BusinessMembership, User } from "@/lib/api/types";
import { qk } from "@/lib/query-keys";

type AuthStatus = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  businesses: BusinessMembership[];
  isStaff: boolean;
  /** Adopt a fresh token pair from login / signup / passkey. */
  adoptSession: (session: AuthSession) => Promise<void>;
  signOut: (opts?: { revoke?: boolean }) => Promise<void>;
  /** Re-read /auth/me — needed after email verification and role changes. */
  reloadIdentity: () => Promise<void>;
  /** Verified status is baked into the access token, so refresh then reload. */
  refreshTokenAndIdentity: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [hasSession, setHasSession] = React.useState(() => Boolean(tokens.getRefresh()));

  // The server can end a session at any time (revoked, password changed, replay
  // detected). The client wrapper fires this; we drop all cached data with it.
  React.useEffect(() => {
    const unsubscribe = tokens.onSessionEnded(() => {
      setHasSession(false);
      queryClient.clear();
    });
    return () => void unsubscribe();
  }, [queryClient]);

  // Another tab signing out should sign this one out too.
  React.useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== storageKeys.refresh) return;
      if (e.newValue === null) {
        tokens.clear();
        setHasSession(false);
        queryClient.clear();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [queryClient]);

  const meQuery = useQuery({
    queryKey: qk.me,
    queryFn: authApi.me,
    enabled: hasSession,
    staleTime: 60_000,
    retry: (count, error) => {
      if (isApiError(error) && error.status === 401) return false;
      return count < 1;
    },
  });

  React.useEffect(() => {
    if (meQuery.error && isApiError(meQuery.error) && meQuery.error.status === 401) {
      tokens.clear();
      setHasSession(false);
    }
  }, [meQuery.error]);

  const adoptSession = React.useCallback(
    async (session: AuthSession) => {
      tokens.set(session);
      setHasSession(true);
      // Seed the cache so the first paint after sign-in has an identity, then
      // let /auth/me fill in the full business list.
      queryClient.setQueryData(qk.me, {
        user: session.user,
        businesses: session.business
          ? [{ ...session.business, role: "owner" as const }]
          : [],
      });
      if (session.business) writeActiveBusinessId(session.business.id);
      await queryClient.invalidateQueries({ queryKey: qk.me });
    },
    [queryClient],
  );

  const signOut = React.useCallback(
    async ({ revoke = true }: { revoke?: boolean } = {}) => {
      const refreshToken = tokens.getRefresh();
      tokens.clear();
      setHasSession(false);
      writeActiveBusinessId(null);
      queryClient.clear();
      if (revoke && refreshToken) {
        // Fire and forget: the local session is already gone either way.
        await authApi.logout(refreshToken).catch(() => undefined);
      }
    },
    [queryClient],
  );

  const reloadIdentity = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: qk.me });
  }, [queryClient]);

  const refreshTokenAndIdentity = React.useCallback(async () => {
    await refreshSession();
    await queryClient.invalidateQueries({ queryKey: qk.me });
  }, [queryClient]);

  const status: AuthStatus = !hasSession
    ? "anonymous"
    : meQuery.data
      ? "authenticated"
      : meQuery.isError
        ? "anonymous"
        : "loading";

  const value = React.useMemo<AuthContextValue>(
    () => ({
      status,
      user: meQuery.data?.user ?? null,
      businesses: meQuery.data?.businesses ?? [],
      isStaff: Boolean(meQuery.data?.user.staffRole),
      adoptSession,
      signOut,
      reloadIdentity,
      refreshTokenAndIdentity,
    }),
    [status, meQuery.data, adoptSession, signOut, reloadIdentity, refreshTokenAndIdentity],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
